import { useEffect, useId } from "react";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import { HANDLEBARS_INLINE_ATTR, HANDLEBARS_TEMPLATE_ATTR } from "./constants";
import { useDynamicNodeContext } from "./dynamic-node-context";
import { renderMarkdownToHtml } from "./render-markdown";
import { collectReferencedKeys } from "./resolution";
import "./dynamic-nodes.css";

/**
 * Read-only view of a handlebars (conditional / helper / loop) node. Evaluates the
 * stored template against the current answer context and renders the result.
 * Inline fragments render as text; block fragments render their evaluated markdown.
 */
export const HandlebarsNodeView: React.FC<NodeViewProps> = ({ node }) => {
  const template = (node.attrs[HANDLEBARS_TEMPLATE_ATTR] as string) ?? "";
  const inline = node.attrs[HANDLEBARS_INLINE_ATTR] !== false;
  const { evaluateHandlebars, frozen, registerKeys, unregisterKeys } =
    useDynamicNodeContext();
  const nodeId = useId();

  // A conditional/interpolation references data keys whose values it needs; ask
  // the backend for all of them so the fragment can evaluate correctly.
  useEffect(() => {
    registerKeys(nodeId, collectReferencedKeys(template));
    return () => unregisterKeys(nodeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const rendered = evaluateHandlebars(template);

  const className = [
    "tiptap-handlebars",
    inline ? "tiptap-handlebars--inline" : "tiptap-handlebars--block",
    frozen ? "tiptap-handlebars--frozen" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <NodeViewWrapper
      as={inline ? "span" : "div"}
      className={className}
      contentEditable={false}
      data-handlebars-template={template}
    >
      {inline ? (
        rendered
      ) : (
        <div
          data-color-mode="light"
          dangerouslySetInnerHTML={{
            __html: renderMarkdownToHtml(rendered).html,
          }}
        />
      )}
    </NodeViewWrapper>
  );
};

export default HandlebarsNodeView;
